import { Button } from "@/components/ui/button.tsx"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover.tsx"
import { classes } from "@/utils"
import { Check, ChevronsUpDown, Star } from "lucide-react"
import { useMemo, useRef, useState, useEffect } from "preact/hooks"

export interface MappingCustomerOption {
  stable_key: string
  name: string
}

export interface CustomerMatchSuggestion {
  customer_stable_key: string
  score: number
  match_summary: string
}

interface ForecastCustomerMapSelectProps {
  nodes: MappingCustomerOption[]
  suggestions: CustomerMatchSuggestion[]
  value: string
  onValueChange: (stableKey: string) => void
  className?: string
}

function node_label(n: MappingCustomerOption): string {
  return n.name
}

function node_search_blob(n: MappingCustomerOption): string {
  return `${n.name} ${n.stable_key}`.toLowerCase()
}

function get_search_tokens(q: string): string[] {
  return q
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
}

function node_matches_search(n: MappingCustomerOption, tokens: string[]): boolean {
  if (tokens.length === 0) return true
  const blob = node_search_blob(n)
  return tokens.every((t) => blob.includes(t))
}

function score_color(score: number): string {
  if (score >= 0.8) return "text-green-600"
  if (score >= 0.55) return "text-yellow-700"
  return "text-gray-600"
}

export function ForecastCustomerMapSelect({
  nodes,
  suggestions,
  value,
  onValueChange,
  className,
}: ForecastCustomerMapSelectProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const commandListRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchValue), 150)
    return () => clearTimeout(t)
  }, [searchValue])

  const suggestionByKey = useMemo(
    () => new Map(suggestions.map((s) => [s.customer_stable_key, s])),
    [suggestions]
  )

  const orderedNodes = useMemo(() => {
    const sugKeys = new Set(suggestions.map((s) => s.customer_stable_key))
    const suggested = suggestions
      .map((s) => nodes.find((n) => n.stable_key === s.customer_stable_key))
      .filter((n): n is MappingCustomerOption => n != null)
    const rest = nodes.filter((n) => !sugKeys.has(n.stable_key)).sort((a, b) => a.name.localeCompare(b.name))
    return [...suggested, ...rest]
  }, [nodes, suggestions])

  const filtered = useMemo(() => {
    const tokens = get_search_tokens(debouncedSearch)
    return orderedNodes.filter((n) => node_matches_search(n, tokens))
  }, [orderedNodes, debouncedSearch])

  const selected = value ? nodes.find((n) => n.stable_key === value) : undefined
  const selectedSug = value ? suggestionByKey.get(value) : undefined

  function stop_wheel_bubbling(e: WheelEvent) {
    e.stopPropagation()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={classes(
            "min-w-[260px] justify-start text-left font-normal h-9 text-sm",
            className ?? ""
          )}>
          <div className="flex items-center gap-2 truncate w-full min-w-0">
            {selected ? (
              <>
                {selectedSug ? (
                  <Star className="h-3.5 w-3.5 text-yellow-500 shrink-0" />
                ) : (
                  <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500" />
                )}
                <span className="truncate flex-1 min-w-0">{node_label(selected)}</span>
                {selectedSug ? (
                  <span className={classes("text-xs font-medium shrink-0", score_color(selectedSug.score))}>
                    {Math.round(selectedSug.score * 100)}%
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <ChevronsUpDown className="h-4 w-4 shrink-0 text-gray-500" />
                <span className="text-gray-500">Search customers…</span>
              </>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="z-[100] w-[min(420px,92vw)] p-0 overflow-hidden"
        align="start"
        onWheel={stop_wheel_bubbling}>
        <Command shouldFilter={false} className="max-h-[min(400px,55vh)] flex flex-col">
          <CommandInput
            placeholder="Type to search…"
            value={searchValue}
            onValueChange={setSearchValue}
            className="h-9 text-sm"
          />
          <CommandList
            ref={commandListRef}
            className="max-h-[min(340px,50vh)] overflow-y-auto overscroll-y-contain"
            onWheel={stop_wheel_bubbling}>
            {debouncedSearch !== searchValue ? (
              <CommandEmpty className="text-sm py-6">Searching…</CommandEmpty>
            ) : filtered.length === 0 ? (
              <CommandEmpty className="text-sm py-6">No customers match.</CommandEmpty>
            ) : (
              <CommandGroup>
                {filtered.map((n, index) => {
                  const sug = suggestionByKey.get(n.stable_key)
                  const isPick = value === n.stable_key
                  return (
                    <CommandItem
                      key={n.stable_key}
                      value={`${node_search_blob(n)} ${n.stable_key}`}
                      className={classes(
                        index % 2 === 0 ? "bg-slate-50/80" : "",
                        isPick ? "bg-blue-50" : ""
                      )}
                      onSelect={() => {
                        onValueChange(n.stable_key)
                        setOpen(false)
                      }}>
                      <Check className={classes("mr-2 h-4 w-4 shrink-0", isPick ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-1 min-w-0 flex-col gap-0.5">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium truncate flex-1 min-w-0">{n.name}</span>
                          {sug ? (
                            <span className="flex items-center gap-0.5 shrink-0 text-xs">
                              <Star className="h-3 w-3 text-yellow-500" />
                              <span className={classes("font-medium", score_color(sug.score))}>
                                {Math.round(sug.score * 100)}%
                              </span>
                            </span>
                          ) : null}
                        </div>
                        {sug?.match_summary ? (
                          <div className="text-xs text-gray-600 truncate">{sug.match_summary}</div>
                        ) : (
                          <div className="text-xs text-gray-500 truncate font-mono">{n.stable_key}</div>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("node_matches_search", () => {
    const n: MappingCustomerOption = { stable_key: "k1", name: "Target Corporation" }

    it("matches tokens in name", () => {
      expect(node_matches_search(n, ["target"])).toBe(true)
    })

    it("matches stable key substring", () => {
      expect(node_matches_search(n, ["k1"])).toBe(true)
    })
  })
}
